import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Toast.scss';

const Toast = ({ message, type = 'info', onClose, autoClose = true, autoCloseDelay = 5000 }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Track closing animation timer to prevent memory leaks
  const closeTimerRef = useRef(null);
  // Track auto-close timer to prevent race conditions
  const autoCloseTimerRef = useRef(null);

  // Trigger opening animation after component mounts
  useEffect(() => {
    const animationFrame = requestAnimationFrame(() => {
      setIsAnimating(true);
    });
    
    return () => cancelAnimationFrame(animationFrame);
  }, []);

  // Handle close with proper timer management
  const handleClose = useCallback(() => {
    // Prevent multiple close calls
    if (closeTimerRef.current) return;
    
    // Clear auto-close timer if it exists to prevent race condition
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
    
    setIsClosing(true);
    closeTimerRef.current = setTimeout(() => {
      onClose();
    }, 300); // Match CSS animation duration
  }, [onClose]);

  // Auto-close functionality
  useEffect(() => {
    if (autoClose) {
      autoCloseTimerRef.current = setTimeout(handleClose, autoCloseDelay);
      return () => {
        if (autoCloseTimerRef.current) {
          clearTimeout(autoCloseTimerRef.current);
          autoCloseTimerRef.current = null;
        }
      };
    }
  }, [autoClose, autoCloseDelay, handleClose]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
      }
    };
  }, []);

  const getTypeClass = () => {
    switch (type) {
      case 'success': return 'toast--success';
      case 'error': return 'toast--error';
      case 'warning': return 'toast--warning';
      case 'info':
      default: return 'toast--info';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success': return '✓';
      case 'error': return '✕';
      case 'warning': return '⚠';
      case 'info':
      default: return 'ℹ';
    }
  };

  return (
    <div 
      className={`toast ${getTypeClass()} ${isClosing ? 'toast--closing' : isAnimating ? 'toast--opening' : ''}`}
      onClick={handleClose}
    >
      <div className="toast__icon">
        {getIcon()}
      </div>
      <div className="toast__content">
        <div className="toast__message">
          {message}
        </div>
      </div>
      <button 
        className="toast__close-button"
        onClick={(e) => {
          e.stopPropagation();
          handleClose();
        }}
        aria-label="Close notification"
      >
        ×
      </button>
    </div>
  );
};

export default Toast;